import React from 'react';
import { FileText, Calendar, Download, Eye } from 'lucide-react';

export interface Report {
    id: number;
    report_type: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY';
    title: string;
    period_start: string;
    period_end: string;
    publication_date: string;
    summary_html?: string;
    file_url?: string;
    key_stats_json?: string;
    is_published?: boolean;
}

interface ReportCardProps {
    report: Report;
    onView: (report: Report) => void;
    onDownload: (report: Report) => void;
}

const ReportCard: React.FC<ReportCardProps> = ({ report, onView, onDownload }) => {

    // Badge Color Helper
    const getBadgeStyle = (type: string) => {
        switch (type) {
            case 'WEEKLY': return { bg: '#e0f2fe', color: '#0369a1', label: 'Hebdo' };
            case 'MONTHLY': return { bg: '#dcfce7', color: '#166534', label: 'Mensuel' };
            case 'QUARTERLY': return { bg: '#f3e8ff', color: '#6b21a8', label: 'Trimestriel' };
            case 'DAILY': return { bg: '#fff7ed', color: '#c2410c', label: 'Flash' };
            default: return { bg: '#f1f5f9', color: '#475569', label: 'Rapport' };
        }
    };

    const badge = getBadgeStyle(report.report_type);

    // Format Date Range
    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };
    const dateRange = `${formatDate(report.period_start)} - ${formatDate(report.period_end)} ${new Date(report.period_end).getFullYear()}`;

    // Extract snippet from HTML (strip tags)
    const getSnippet = (html?: string) => {
        if (!html) return "Pas de résumé disponible.";
        const tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        const text = tmp.textContent || tmp.innerText || "";
        return text.substring(0, 120) + "...";
    };

    return (
        <div style={{
            background: 'var(--bg-box)',
            borderRadius: '16px',
            border: '1px solid var(--border-color)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'default'
        }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.05)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {/* Header: Badge + Date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                    background: badge.bg,
                    color: badge.color,
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                }}>
                    {badge.label}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {new Date(report.publication_date).toLocaleDateString('fr-FR')}
                </span>
            </div>

            {/* Content */}
            <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-heading)', marginBottom: '6px', lineHeight: '1.4' }}>
                    {report.title}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '13px', marginBottom: '12px' }}>
                    <Calendar size={14} />
                    <span>{dateRange}</span>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5', margin: 0 }}>
                    {getSnippet(report.summary_html)}
                </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                    onClick={() => onView(report)}
                    style={{
                        flex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        background: 'var(--bg-input)', border: '1px solid var(--border-color)',
                        color: 'var(--text-main)', borderRadius: '8px', padding: '8px',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-input)'}
                >
                    <Eye size={16} /> Lire
                </button>
                {report.file_url && (
                    <button
                        onClick={() => onDownload(report)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(2, 132, 199, 0.1)', border: '1px solid rgba(2, 132, 199, 0.3)',
                            color: '#0ea5e9', borderRadius: '8px', padding: '8px 12px',
                            cursor: 'pointer', transition: 'background 0.2s'
                        }}
                        title="Télécharger PDF"
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(2, 132, 199, 0.2)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(2, 132, 199, 0.1)'}
                    >
                        <Download size={18} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ReportCard;

